import OwButton from "@components/shared/OwButton/OwButton";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { ReactHookInput } from "@components/shared/ReactHookInput/ReactHookInput";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import UserContext from "@contexts/UserContext";
import IOnProcessCustomImportDTO from "@models/DTO/IOnProcessCustomImportDTO";
import IProcessCustomImportDTO from "@models/DTO/IProcessCustomImportDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import ICustomImportUI from "@models/ICustomImportUI";
import INewCustomImport from "@models/INewCustomImport";
import INotification from "@models/INotification";
import IUiSocketSetup from "@models/IUiSocketSetup";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import { useContext, useEffect, useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import "./CustomImportSidebar.scss";

type Props = {
    closeFn: (refresh?: boolean) => void;
    existingCustomImport?: ICustomImportUI;
};

// Create a base validation schema that works for both types
type FormData = ICustomImportUI | INewCustomImport;

const validationSchema = Yup.object().shape({
    supplier_id: Yup.number().required("Supplier ID is required"),
    name: Yup.string().required("Name is required"),
    has_header_row: Yup.boolean().required("Header row selection is required"),
    type: Yup.mixed<"Order" | "Bulk Orders" | "Accounts">()
        .required("Type is required")
        .oneOf(["Order", "Bulk Orders", "Accounts"], "Invalid type selected"),
    import_cols: Yup.mixed()
        .required("Import columns are required")
        .test("is-valid-json", "Must be valid JSON array", (value) => {
            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed);
                } catch {
                    return false;
                }
            }
            return Array.isArray(value);
        })
        .test("min-length", "At least one import column is required", (value) => {
            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed) && parsed.length >= 1;
                } catch {
                    return false;
                }
            }
            return Array.isArray(value) && value.length >= 1;
        }),
    import_steps: Yup.mixed()
        .required("Import steps are required")
        .test("is-valid-json", "Must be valid JSON array", (value) => {
            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed);
                } catch {
                    return false;
                }
            }
            return Array.isArray(value);
        })
        .test("min-length", "At least one import step is required", (value) => {
            if (typeof value === "string") {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed) && parsed.length >= 1;
                } catch {
                    return false;
                }
            }
            return Array.isArray(value) && value.length >= 1;
        }),
    // Optional fields from ICustomImportUI
    id: Yup.number().optional(),
    guid: Yup.string().optional(),
    deleteFlag: Yup.boolean().optional(),
    created_at: Yup.mixed<Date | string>().optional(),
    updated_at: Yup.mixed<Date | string>().optional(),
});

export const CustomImportSidebar = (props: Props) => {
    const uiContext = useContext(UiContext);
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors, isValid },
        watch,
    } = useForm<FormData>({
        // This is a workaround to fix the type error - the union type is causing issues with the resolver type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resolver: yupResolver(validationSchema) as any,
        defaultValues:
            props.existingCustomImport ||
            ({
                supplier_id: supplierModel.id,
                import_cols: [],
                import_steps: [],
                type: "Order",
                has_header_row: true,
                name: "",
            } as INewCustomImport),
    });

    const [debugExpanded, setDebugExpanded] = useState(false);

    const onSubmit: SubmitHandler<FormData> = (data) => {
        console.log("onSubmit", data);
        processCustomImport(data);
    };

    // Add this to debug validation errors
    const onError = (errors: any) => {
        console.error("Form validation errors:", errors);
    };

    const processCustomImport = (data: ICustomImportUI | INewCustomImport) => {
        const payload: IProcessCustomImportDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            customImport: data,
        };

        socket.emit(EndpointsEnum.CustomImportProcessCustomImport, payload);
    };

    const onProcessCustomImport = (data: IOnProcessCustomImportDTO) => {
        const notification: INotification = {
            message: "successfully_processed_custom_import",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        props.closeFn(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CustomImportOnProcessCustomImport, fn: onProcessCustomImport }];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    return (
        <div className={"CustomImportSidebar d-flex flex-column h-100"}>
            {/* Fixed Header */}
            <div className="sidebar-header p-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                    <OwTitle title="custom_import" leftIconName="fad fa-plus" />
                    <div>
                        <OwButton className="btn-danger" centerIconClass="fad fa-times" fn={() => props.closeFn()} />
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit, onError)} className="d-flex flex-column flex-grow-1 overflow-hidden">
                {/* Scrollable Content */}
                <div className="sidebar-content flex-grow-1 overflow-auto p-3">
                    <input type="hidden" {...register("supplier_id")} />
                    <div className="form-group mb-2">
                        <OwLabel title="name" isBold={true} showIsReqAsterik={true} />
                        <ReactHookInput
                            register={register("name")}
                            name={"name"}
                            className={"form-control-sm"}
                            type={"text"}
                            defaultClassName={"form-control"}
                        />
                        {errors.name && <small className="text-danger">{errors.name.message}</small>}
                    </div>
                    <div>
                        <Controller
                            name="has_header_row"
                            control={control}
                            render={({ field }) => (
                                <OwToggle
                                    label="has_header_row"
                                    name="has_header_row"
                                    fn={(e) => {
                                        console.log("OwToggle onChange:", e.target.value);
                                        field.onChange(e.target.value);
                                    }}
                                    active={field.value}
                                />
                            )}
                        />
                        {errors.has_header_row && <small className="text-danger">{errors.has_header_row.message}</small>}
                    </div>
                    <div className="form-group mb-2">
                        <OwLabel title="type" isBold={true} showIsReqAsterik={true} />
                        <select className="form-control" {...register("type")}>
                            <option value="Order">Order</option>
                            <option value="Bulk Orders">Bulk Orders</option>
                            <option value="Accounts">Accounts</option>
                        </select>
                        {errors.type && <small className="text-danger">{errors.type.message}</small>}
                    </div>
                    <div className="form-group mb-2">
                        <OwLabel title="import_cols" isBold={true} showIsReqAsterik={true} />
                        <Controller
                            name="import_cols"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <textarea
                                        className="form-control"
                                        rows={10}
                                        value={typeof field.value === "string" ? field.value : JSON.stringify(field.value || [], null, 2)}
                                        onChange={(e) => {
                                            try {
                                                const parsed = JSON.parse(e.target.value);
                                                field.onChange(parsed);
                                            } catch {
                                                // If JSON is invalid, keep the string value for the user to fix
                                                field.onChange(e.target.value);
                                            }
                                        }}
                                        onBlur={field.onBlur}
                                    />
                                    {fieldState.error && <small className="text-danger">{fieldState.error.message}</small>}
                                </>
                            )}
                        />
                    </div>
                    <div className="form-group mb-2">
                        <OwLabel title="import_steps" isBold={true} showIsReqAsterik={true} />
                        <Controller
                            name="import_steps"
                            control={control}
                            render={({ field, fieldState }) => (
                                <>
                                    <textarea
                                        className="form-control"
                                        rows={10}
                                        value={typeof field.value === "string" ? field.value : JSON.stringify(field.value || [], null, 2)}
                                        onChange={(e) => {
                                            try {
                                                const parsed = JSON.parse(e.target.value);
                                                field.onChange(parsed);
                                            } catch {
                                                // If JSON is invalid, keep the string value for the user to fix
                                                field.onChange(e.target.value);
                                            }
                                        }}
                                        onBlur={field.onBlur}
                                    />
                                    {fieldState.error && <small className="text-danger">{fieldState.error.message}</small>}
                                </>
                            )}
                        />
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="sidebar-footer border-top">
                    {/* Debug panel - remove this in production */}
                    <div className="debug-panel p-2 border-bottom">
                        <div
                            className="d-flex justify-content-between align-items-center cursor-pointer"
                            onClick={() => setDebugExpanded(!debugExpanded)}>
                            <small className="text-muted d-flex align-items-center">
                                <i className={`fas fa-chevron-${debugExpanded ? "down" : "right"} me-2`}></i>
                                <strong>Form Valid:</strong> <span className="ms-1">{isValid ? "✅" : "❌"}</span>
                            </small>
                        </div>
                        {debugExpanded && (
                            <div className="mt-2">
                                <small className="text-muted">
                                    <strong>Errors:</strong>{" "}
                                    {Object.keys(errors).length > 0 ? (
                                        <ul className="mb-1 mt-1">
                                            {Object.entries(errors).map(([field, error]) => (
                                                <li key={field}>
                                                    {field}: {(error as any)?.message || "Invalid"}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        "None"
                                    )}
                                    <br />
                                    <strong>Current Values:</strong>
                                    <pre className="mt-1" style={{ fontSize: "0.8em", maxHeight: "150px", overflow: "auto" }}>
                                        {JSON.stringify(watch(), null, 2)}
                                    </pre>
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="p-3">
                        <OwButton type="submit" className="btn-primary" title="save" centerIconClass="fad fa-save" />
                    </div>
                </div>
            </form>
        </div>
    );
};
